import { For, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import { getPortfolioFileContentBlob, type CoworkPortfolioReport } from "@/cowork/api"
import { ExtractedFields } from "./extracted-fields-table"

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)

export function ReportAccountingReview(props: { report: CoworkPortfolioReport }) {
  const language = useLanguage()
  const [state, setState] = createStore({ error: false, downloading: "" })
  const findings = () => props.report.validation_results ?? []
  const review = () => props.report.artifact_metadata?.accounting_review
  const history = () => {
    const value = review()
    return record(value) && Array.isArray(value.repair_history) ? value.repair_history : []
  }
  const files = () => {
    const value = props.report.artifact_metadata?.source_files
    return Array.isArray(value) ? value.filter(record) : []
  }
  const sources = (finding: Record<string, unknown>) => {
    const refs = Array.isArray(finding.evidence_refs) ? finding.evidence_refs : []
    const ids = new Set(refs.flatMap((ref) => typeof ref === "string" ? [/^file:([^:#]+)/.exec(ref)?.[1]] : []))
    return files().filter((file) => typeof file.id === "string" && ids.has(file.id))
  }
  const download = async (file: Record<string, unknown>) => {
    if (typeof file.id !== "string" || typeof file.filename !== "string") return
    setState({ error: false, downloading: file.id })
    try {
      const blob = await getPortfolioFileContentBlob(file.id)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = file.filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      setState("error", true)
    } finally {
      setState("downloading", "")
    }
  }
  return (
    <Show when={findings().length || review()}>
      <details class="my-3 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 text-[12px]">
        <summary class="cursor-pointer font-semibold">{language.t("cowork.accounting.overview.checks")}</summary>
        <Show when={state.error}><p role="alert">{language.t("common.requestFailed")}</p></Show>
        <div class="mt-3 space-y-3">
          <Show when={findings().length} fallback={<p>{language.t("cowork.accounting.overview.noWarnings")}</p>}>
            <For each={findings()}>{(finding) => (
              <section class="space-y-2 border-b border-v2-border-border-base pb-3">
                <p class="whitespace-pre-wrap">{String(finding.message ?? finding.code ?? "")}</p>
                <For each={sources(finding)}>{(file) => (
                  <button type="button" class="mr-3 underline" disabled={state.downloading === file.id}
                    onClick={() => void download(file)}>{String(file.filename)}</button>
                )}</For>
                <Show when={record(finding.details) && Object.keys(finding.details).length}>
                  <details>
                    <summary class="cursor-pointer">{language.t("cowork.enterprise.audit.viewDetails")}</summary>
                    <ExtractedFields value={finding.details} />
                  </details>
                </Show>
              </section>
            )}</For>
          </Show>
          <Show when={history().length}>
            <h3 class="font-semibold">{language.t("cowork.enterprise.workflows.reporting.history")}</h3>
            <ExtractedFields value={history()} />
          </Show>
        </div>
      </details>
    </Show>
  )
}
